import type {
  AllergenDto,
  CourseDto,
  MenuItemVariantDto,
  MenuSectionDto,
  MenuSubSectionDto,
  OptionDto,
  OptionGroupDto,
} from '../../generated-api';
import { OptionGroupDtoProductTypeEnum, OptionGroupDtoSelectionTypeEnum } from '../../generated-api';
import type {
  CourseDto as PlainCourseDto,
  PlainAllergenDto,
  PlainItemDto,
  PlainMenuDto,
  PlainOpeningHoursDto,
  PlainOptionDto,
  PlainOptionGroupDto,
  PlainSectionDto,
  PlainVariantOptionDto,
  PlainVariantOptionGroupDto,
  PlainSubSectionDto,
} from '@ordr-as/menu-builder-types/epos';

// Valid enum values for runtime validation
const VALID_SELECTION_TYPES = Object.values(OptionGroupDtoSelectionTypeEnum);
const VALID_PRODUCT_TYPES = Object.values(OptionGroupDtoProductTypeEnum);

function toSelectionType(value: string): OptionGroupDtoSelectionTypeEnum {
  if (VALID_SELECTION_TYPES.includes(value as OptionGroupDtoSelectionTypeEnum)) {
    return value as OptionGroupDtoSelectionTypeEnum;
  }
  console.warn(`Invalid selectionType "${value}", defaulting to SINGLE_CHOICE`);
  return OptionGroupDtoSelectionTypeEnum.SINGLE_CHOICE;
}

function toProductType(value: string): OptionGroupDtoProductTypeEnum {
  if (VALID_PRODUCT_TYPES.includes(value as OptionGroupDtoProductTypeEnum)) {
    return value as OptionGroupDtoProductTypeEnum;
  }
  console.warn(`Invalid productType "${value}", defaulting to ADDON`);
  return OptionGroupDtoProductTypeEnum.ADDON;
}

export interface MappedMenuData {
  activeSections: MenuSectionDto[];
  courses: CourseDto[];
  componentProducts: MenuItemVariantDto[];
  openingHours: Record<string, PlainOpeningHoursDto[]>;
  searchableSections: MenuSectionDto[];
}

export function mapPlainMenuToMenuData(plainMenu: PlainMenuDto): MappedMenuData {
  const searchableSections = [...plainMenu.sections, ...((plainMenu as any).searchOnlySections ?? [])];

  // Filter by active only - opening hours filtering is done at runtime via useActiveSections
  const activeSections = plainMenu.sections.filter((section) => section.active);
  const activeSectionsWithSearchOnly = searchableSections.filter((section) => section.active);

  // Collect opening hours for all sections for filtering at read time
  const openingHours: Record<string, PlainOpeningHoursDto[]> = {};
  for (const section of searchableSections) {
    if (section.hasOpeningHours && section.openingHours.length > 0) {
      openingHours[section.sectionId] = section.openingHours;
    }
  }

  return {
    activeSections: activeSections.map(mapPlainSectionToMenuSection),
    courses: plainMenu.courses.map(mapCourse),
    openingHours,
    searchableSections: activeSectionsWithSearchOnly.map(mapPlainSectionToMenuSection),
    componentProducts: plainMenu.componentProducts.map(mapPlainItemToMenuItemVariant),
  };
}

function mapCourse(course: PlainCourseDto): CourseDto {
  return {
    courseId: course.courseId ?? '',
    name: course.name,
    sequence: course.sequence ?? 0,
  };
}

function mapPlainSectionToMenuSection(section: PlainSectionDto): MenuSectionDto {
  return {
    sectionId: section.sectionId,
    menuId: section.menuId,
    name: section.name,
    locationId: section.locationId ?? 0,
    locationName: section.locationName,
    eposDisplayColor: section.eposDisplayColor ?? undefined,
    items: (section.items ?? []).filter((item) => item.active).map(mapPlainItemToMenuItemVariant),
    subsections: (section.subSections ?? [])
      .filter((subsection) => subsection.items.length > 0)
      .map(mapPlainSubsectionItemToMenuSubsection),
  };
}

function mapPlainItemToMenuItemVariant(item: PlainItemDto): MenuItemVariantDto {
  return {
    variantLinkId: item.variantLinkId,
    name: item.name,
    price: item.price ?? 0,
    takeawayPrice: item.takeawayPrice ?? undefined,
    description: item.description,
    allergens: item.allergens.map(mapPlainAllergenToAllergen),
    // Merge variant option groups (first) with regular option groups
    optionGroups: [
      ...(item.variantOptionGroups ?? []).map((vog) => mapVariantOptionGroup(vog)),
      ...(item.optionGroups ?? []).map(mapPlainOptionGroupToOptionGroup),
    ],
    menuSectionId: item.menuSectionId ?? undefined,
    sectionProductId: item.sectionProductId ?? undefined,
    eposDisplayColor: item.eposDisplayColor ?? undefined,
    allowOpenPrice: item.allowOpenPrice,
    courseId: item.courseId ?? undefined,
    courseName: item.courseName ?? undefined,
    courseSequence: item.courseSequence ?? undefined,
    giftCardId: item.giftCardId ?? undefined,
    giftCardExpirationYears: item.giftCardExpirationYears ?? undefined,
    priceType: transformPriceType(item),
    plu: item.plu ?? undefined,
    gtin: item.gtin ?? undefined,
    abv: item.abv ?? undefined,
    isComposite: item.isComposite ?? false,
    autoApplyBundlePrice: item.autoApplyBundlePrice ?? undefined,
    showComponents: item.showComponents ?? undefined,
    components: (item.components as any) ?? undefined,
  };
}

function mapPlainSubsectionItemToMenuSubsection(item: PlainSubSectionDto): MenuSubSectionDto {
  return {
    id: item.sectionId,
    items: (item.items ?? []).filter((subItem) => subItem.active).map(mapPlainItemToMenuItemVariant),
    sectionType: '',
    sectionOwnerName: '',
    sectionOwnerLocationId: 0,
    name: item.name,
    sequence: item.sectionSequence ?? 0,
    active: item.active ?? true,
    productCount: item.items?.length ?? 0,
  };
}

/**
 * Transform price type values to normalized enum format.
 * Server-side mapping logic replicated from V5 endpoint.
 */
function transformPriceType(item: PlainItemDto): string | undefined {
  if (item.allowOpenPrice) return 'OPEN_PRICE';

  const priceType = item.priceType;
  if (!priceType) return 'PER_ITEM';

  const mapping: Record<string, string> = {
    'Open price': 'OPEN_PRICE',
    'Per item': 'PER_ITEM',
    'Per gram': 'PER_GRAM',
    'Per hg': 'PER_HG',
    'Per kg': 'PER_KG',
  };

  return mapping[priceType] ?? priceType;
}

/**
 * Map variant option group to standard OptionGroupDto.
 * Variant option groups have a different structure and need special handling.
 */
function mapVariantOptionGroup(group: PlainVariantOptionGroupDto): OptionGroupDto {
  return {
    optionGroupId: undefined,
    variantLinkId: group.variantLinkId,
    selectionType: toSelectionType(group.selectionType),
    productType: toProductType(group.menuItemType),
    displayName: group.displayName ?? '',
    description: group.description ?? '',
    maxSelection: 0,
    minSelection: 0,
    required: group.required ?? false,
    // Use the group's variantLinkId (not item's) to determine preSelected - matches LocationV2Dto behavior
    options: (group.variantOptions ?? []).map((vo) => mapVariantOption(vo, group.variantLinkId)),
  };
}

/**
 * Map variant option to standard OptionDto.
 * preSelected is true when this variant is the "parent" variant (default selection).
 */
function mapVariantOption(option: PlainVariantOptionDto, parentVariantLinkId: string): OptionDto {
  return {
    optionId: option.variantId,
    name: option.name,
    price: option.price ?? 0,
    takeawayPrice: option.takeawayPrice ?? undefined,
    preSelected: option.variantId === parentVariantLinkId,
  };
}

function mapPlainAllergenToAllergen(allergen: PlainAllergenDto): AllergenDto {
  return {
    name: allergen.name,
    type: allergen.allergenType,
  };
}

function mapPlainOptionGroupToOptionGroup(group: PlainOptionGroupDto): OptionGroupDto {
  return {
    optionGroupId: group.optionGroupId,
    variantLinkId: group.variantLinkId,
    selectionType: toSelectionType(group.selectionType),
    productType: toProductType(group.productType),
    displayName: group.displayName,
    description: group.description ?? '',
    maxSelection: group.maxSelection ?? 0,
    minSelection: group.minSelection ?? 0,
    required: group.required ?? false,
    options: group.options.map(mapPlainOptionToOption),
  };
}

function mapPlainOptionToOption(option: PlainOptionDto): OptionDto {
  return {
    optionId: option.optionId,
    name: option.name,
    price: option.price ?? 0,
    preSelected: option.preSelected ?? false,
  };
}
